import type { PortalWidget } from "@entrilia/shared/api/portalWidgetsTypes";
import type { PropsWithChildren } from "react";
import React, { useCallback, useMemo } from "react";
import type { ClientPermissionSet, InvestorRelationPortalFeatures, PortalSettings } from "../store/state/user/types";
import { allInvestorRelationPortalFeatures } from "../store/state/user/types";

const ClientContext = React.createContext<ContextValue | undefined>(undefined);

interface ContextValue {
  clientCode: string;
  clientTitle: string;
  contactName: string;
  contactEmail: string;
  portalSettings: PortalSettings | undefined;
  isFeatureEnabled: (feature: InvestorRelationPortalFeatures) => boolean;
  isAnyFeatureEnabled: (features: InvestorRelationPortalFeatures[]) => boolean;
  isFeatureEnabledInLive: (feature: InvestorRelationPortalFeatures) => boolean;
  isFeatureEnabledInPreview: (feature: InvestorRelationPortalFeatures) => boolean;
  dashboardWidgets: PortalWidget[];
  portfolioWidget: PortalWidget | undefined;
  isNewsPageAvailable: boolean;
  isDashboardPageAvailable: boolean;
  impersonationFundName: string;
}

interface Props {
  clientPermissions: ClientPermissionSet;
  impersonationFundName: string;
  isImpersonationMode: boolean;
}

const getEnabledFeatures = (features: string | undefined) =>
  features === undefined
    ? new Set<InvestorRelationPortalFeatures>()
    : new Set<InvestorRelationPortalFeatures>(
        features
          .split(",")
          .map((f) => f.trim())
          .filter((f) =>
            allInvestorRelationPortalFeatures.includes(f as InvestorRelationPortalFeatures)
          ) as InvestorRelationPortalFeatures[]
      );

export const ClientContextProvider = ({
  clientPermissions,
  impersonationFundName,
  isImpersonationMode,
  children,
}: PropsWithChildren<Props>) => {
  const hasAccessByPermissions = useCallback(
    (feature: InvestorRelationPortalFeatures) => {
      switch (feature) {
        case "Inbox": {
          return clientPermissions.messageCategoryIds.length > 0;
        }

        case "PortfolioCompanies": {
          return clientPermissions.investorsPermissions.some((ip) => ip.fundInvestors.length > 0);
        }

        case "Fundraising": {
          return clientPermissions.fundraisings.length > 0;
        }

        case "DataCollection": {
          return clientPermissions.entityPermissions.length > 0;
        }

        default: {
          return true;
        }
      }
    },
    [
      clientPermissions.entityPermissions.length,
      clientPermissions.fundraisings.length,
      clientPermissions.investorsPermissions,
      clientPermissions.messageCategoryIds.length,
    ]
  );

  const enabledLiveFeatures = useMemo(
    () => getEnabledFeatures(clientPermissions.portalSettings?.enabledFeatures),
    [clientPermissions.portalSettings?.enabledFeatures]
  );

  const enabledPreviewFeatures = useMemo(
    () => getEnabledFeatures(clientPermissions.portalSettings?.enabledPreviewOnlyFeatures),
    [clientPermissions.portalSettings?.enabledPreviewOnlyFeatures]
  );

  const isDataCollectionEnabled = clientPermissions.portfolioMonitoringApplication?.dataCollection?.enabled === true;

  const isFeatureEnabledInFeatureSet = useCallback(
    (feature: InvestorRelationPortalFeatures, featureSet: Set<InvestorRelationPortalFeatures>) => {
      if (!hasAccessByPermissions(feature)) {
        return false;
      }

      if (feature === "DataCollection") {
        return isDataCollectionEnabled;
      }

      return featureSet.has(feature);
    },
    [hasAccessByPermissions, isDataCollectionEnabled]
  );

  const isFeatureEnabledInLive = useCallback(
    (feature: InvestorRelationPortalFeatures) => isFeatureEnabledInFeatureSet(feature, enabledLiveFeatures),
    [enabledLiveFeatures, isFeatureEnabledInFeatureSet]
  );

  const isFeatureEnabledInPreview = useCallback(
    (feature: InvestorRelationPortalFeatures) => isFeatureEnabledInFeatureSet(feature, enabledPreviewFeatures),
    [enabledPreviewFeatures, isFeatureEnabledInFeatureSet]
  );

  const isFeatureEnabled = useCallback(
    (feature: InvestorRelationPortalFeatures) => {
      if (isFeatureEnabledInLive(feature)) {
        return true;
      }
      return isImpersonationMode && isFeatureEnabledInPreview(feature);
    },
    [isFeatureEnabledInLive, isFeatureEnabledInPreview, isImpersonationMode]
  );

  const isAnyFeatureEnabled = useCallback(
    (features: InvestorRelationPortalFeatures[]) => features.some((feature) => isFeatureEnabled(feature)),
    [isFeatureEnabled]
  );

  const isNewsPageAvailable =
    clientPermissions.featureToggles.InvestorPortalNewsEnabled === true && isFeatureEnabled("News");

  const dashboardWidgets = useMemo(
    () =>
      clientPermissions.portalSettings?.widgetSettings?.widgets.filter(
        (w) =>
          w.predefinedType === "Balance" ||
          w.predefinedType === "CashFlows" ||
          (w.predefinedType === "News" && isNewsPageAvailable)
      ) ?? [],
    [clientPermissions.portalSettings?.widgetSettings?.widgets, isNewsPageAvailable]
  );

  const isDashboardPageAvailable = isFeatureEnabled("Dashboards") && dashboardWidgets.length > 0;

  const portfolioWidget = useMemo(
    () => clientPermissions.portalSettings?.widgetSettings?.widgets.find((w) => w.predefinedType === "Portfolio"),
    [clientPermissions.portalSettings?.widgetSettings?.widgets]
  );

  return (
    <ClientContext
      value={{
        clientCode: clientPermissions.clientCode,
        clientTitle: clientPermissions.clientTitle,
        contactName: clientPermissions.contactName,
        contactEmail: clientPermissions.contactEmail,
        portalSettings: clientPermissions.portalSettings,
        isFeatureEnabled,
        isAnyFeatureEnabled,
        isFeatureEnabledInLive,
        isFeatureEnabledInPreview,
        dashboardWidgets,
        portfolioWidget,
        isNewsPageAvailable,
        isDashboardPageAvailable,
        impersonationFundName,
      }}
    >
      {children}
    </ClientContext>
  );
};

export default ClientContext;
